/**
 * opportunityMapper.js
 * 
 * Transforma dados de pacientes (paciente → slots disponíveis) 
 * em visão de oportunidades (slot → pacientes compatíveis).
 */

import { calculateRelevanceScore } from './relevanceEngine';

/**
 * Agrupa oportunidades por slot (data+hora).
 * Recebe lista de pacientes com availableDates e retorna
 * lista de slots com seus pacientes compatíveis.
 * 
 * @param {Array} patients - Lista de pacientes da waiting list
 * @param {Object} options - Opções de configuração
 * @param {Function} options.getPatientById - Função para buscar dados do paciente
 * @param {Function} options.getProcedureById - Função para buscar dados do procedimento
 * @param {Function} options.getResourceById - Função para buscar dados do profissional
 * @param {number} options.dailyGoal - Meta diária de atendimentos
 * @returns {Array} Lista de slots ordenados por relevância
 */
export function buildOpportunitiesFromPatients(patients, options = {}) {
  const { getPatientById, getProcedureById, getResourceById, dailyGoal = 8 } = options;
  const slotMap = {};

  patients.forEach((item) => {
    if (!item.availableDates || item.availableDates.length === 0) return;

    const patient = getPatientById?.(item.patientId + '');
    const procedure = getProcedureById?.(item.procedureId);
    const resource = item.resourceId ? getResourceById?.(item.resourceId) : null;

    const waitingDays = item.createdAt
      ? Math.floor((Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    item.availableDates.forEach((ad) => {
      const [date, time] = (ad.datetime || '').split(' ');
      const timeShort = time ? time.substr(0, 5) : '';
      const slotKey = `${date}_${timeShort}`;

      if (!slotMap[slotKey]) {
        slotMap[slotKey] = {
          date,
          time: timeShort,
          datetime: ad.datetime,
          resourceLabel: resource?.Label || null,
          resourceId: item.resourceId,
          patients: [],
        };
      }

      // Calcular score de relevância
      const score = calculateRelevanceScore({
        dailyGoal,
        waitingDays,
        priority: item.priority,
        daysUntilSlot: Math.max(0, Math.floor((new Date(date) - Date.now()) / (1000 * 60 * 60 * 24))),
        preferredProfessional: !!item.resourceId,
      });

      slotMap[slotKey].patients.push({
        waitingListItem: item,
        patientId: item.patientId,
        patientName: patient?.name || 'Desconhecido',
        patientPhone: patient?.phone || '',
        procedureLabel: procedure?.Label || '',
        resourceLabel: resource?.Label || 'Qualquer',
        priority: item.priority,
        waitingDays,
        score,
        status: item.status,
      });
    });
  });

  // Converter map em array e ordenar
  const slots = Object.values(slotMap);

  // Ordenar pacientes dentro de cada slot por score (maior primeiro)
  slots.forEach((slot) => {
    slot.patients.sort((a, b) => b.score - a.score);
    // Score médio do slot para ordenação
    slot.avgScore = slot.patients.length > 0
      ? Math.round(slot.patients.reduce((sum, p) => sum + p.score, 0) / slot.patients.length)
      : 0;
    slot.patientCount = slot.patients.length;
  });

  // Ordenar slots: mais pacientes compatíveis primeiro, depois por score
  slots.sort((a, b) => {
    if (b.patientCount !== a.patientCount) return b.patientCount - a.patientCount;
    if (b.avgScore !== a.avgScore) return b.avgScore - a.avgScore;
    return new Date(a.datetime) - new Date(b.datetime);
  });

  return slots;
}
